// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import CheckmarkOutline from "@eisinc/XDL/Icons/CheckmarkOutline";
import Delete from "@eisinc/XDL/Icons/Delete";
import Edit from "@eisinc/XDL/Icons/Edit";
import Print from "@eisinc/XDL/Icons/Print";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import approveOrder from "@insite/client-framework/Store/Pages/OrderApprovalDetails/Handlers/ApproveOrder";
import deleteOrder from "@insite/client-framework/Store/Pages/OrderApprovalDetails/Handlers/DeleteOrder";
import editOrder from "@insite/client-framework/Store/Pages/OrderApprovalDetails/Handlers/EditOrder";
import requestRevision from "@insite/client-framework/Store/Pages/OrderApprovalDetails/Handlers/RequestRevision";
import translate from "@insite/client-framework/Translate";
import { CartModel } from "@insite/client-framework/Types/ApiModels";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import ActivityLog from "@insite/content-library/Widgets/OrderApprovalDetails/ActivityLog";
import Button from "@insite/mobius/Button";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import OverflowMenu, { OverflowMenuProps } from "@insite/mobius/OverflowMenu/OverflowMenu";
import TextArea, { TextAreaPresentationProps } from "@insite/mobius/TextArea";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: CartModel;
    extendedStyles?: OrderApprovalDetailsActionButtonsStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
    account: getCurrentAccountState(state)?.value,
    cartPageLink: getPageLinkByPageType(state, "CartPage"),
    orderApprovalListPageLink: getPageLinkByPageType(state, "OrderApprovalListPage"),
});

const mapDispatchToProps = {
    approveOrder,
    deleteOrder,
    editOrder,
    requestRevision,
};

export interface OrderApprovalDetailsActionButtonsStyles {
    buttonsHiddenContainer?: HiddenProps;
    menuHiddenContainer?: HiddenProps;
    narrowOverflowMenu?: OverflowMenuProps;
    printButton?: ButtonPresentationProps;
    showActivityLogButton?: ButtonPresentationProps;
    continueShoppingButton?: ButtonPresentationProps;
    deleteButton?: ButtonPresentationProps;
    editButton?: ButtonPresentationProps;
    requestRevisionButton?: ButtonPresentationProps;
    approveButton?: ButtonPresentationProps;
    printClickable?: ClickableProps;
    showActivityLogClickable?: ClickableProps;
    continueShoppingClickable?: ClickableProps;
    deleteClickable?: ClickableProps;
    editClickable?: ClickableProps;
    requestRevisionClickable?: ClickableProps;
    approveClickable?: ClickableProps;
    twoButtonModalStyles?: TwoButtonModalStyles;
    requestRevisionModal?: ModalPresentationProps;
    requestRevisionContainer?: GridContainerProps;
    requestRevisionMessageGridItem?: GridItemProps;
    requestRevisionMessageText?: TypographyPresentationProps;
    requestRevisionMessageTextArea?: TextAreaPresentationProps;
    requestRevisionButtonsGridItem?: GridItemProps;
    requestRevisionSendRequestButton?: ButtonPresentationProps;
    requestRevisionCancelButton?: ButtonPresentationProps;
    activityLogDrawer?: DrawerPresentationProps;
    // XNG-Changes
    buttonWrapper?: InjectableCss;
    iconStyle?: IconPresentationProps;
}

export const orderApprovalDetailsActionButtonsStyles: OrderApprovalDetailsActionButtonsStyles = {
    deleteButton: {
        css: css`
            margin-left: 10px;
        `,
        buttonType: "outline",
        variant: "secondary",
    },
    showActivityLogButton: {
        css: css`
            margin-left: 10px;
        `,
        buttonType: "outline",
        variant: "secondary",
    },
    editButton: {
        css: css`
            margin-left: 10px;
        `,
        buttonType: "outline",
        variant: "secondary",
    },
    requestRevisionButton: {
        css: css`
            margin-left: 10px;
        `,
        buttonType: "outline",
        variant: "secondary",
    },
    continueShoppingButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    approveButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    printButton: {
        variant: "secondary",
        buttonType: "solid",
    },
    buttonsHiddenContainer: {
        below: "lg",
    },
    menuHiddenContainer: {
        above: "md",
    },
    requestRevisionModal: {
        size: 600,
        cssOverrides: {
            modalContent: css`
                padding: 15px 30px 20px 30px;
            `,
        },
    },
    requestRevisionContainer: {
        gap: 10,
    },
    requestRevisionMessageGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    requestRevisionMessageText: {
        css: css`
            margin-bottom: 20px;
        `,
    },
    requestRevisionButtonsGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    requestRevisionCancelButton: {
        variant: "secondary",
        css: css`
            margin-right: 10px;
        `,
    },
    activityLogDrawer: {
        size: 400,
        closeButtonProps: {
            shape: "pill",
            buttonType: "solid",
            color: "common.background",
            size: 36,
        },
        cssOverrides: {
            drawerTitle: css`
                background: ${getColor("common.background")};
            `,
            drawerBody: css`
                background: ${getColor("common.background")};
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                width: 100%;
                            `,
                        ],
                        "max",
                    )}
            `,
            drawerContent: css`
                overflow-y: initial;
            `,
            headlineTypography: css`
                margin: 0 0 0 20px;
            `,
        },
        headlineTypographyProps: {
            color: "text.main",
            size: 18,
            weight: 600,
        },
    },
};

type Props = OwnProps &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

const OrderApprovalDetailsActionButtons = ({
    cart,
    history,
    cartPageLink,
    orderApprovalListPageLink,
    approveOrder,
    deleteOrder,
    editOrder,
    requestRevision,
    extendedStyles,
    account,
    toaster,
}: Props) => {
    const [styles] = useState(() => mergeToNew(orderApprovalDetailsActionButtonsStyles, extendedStyles));
    const [confirmationModalIsOpen, setConfirmationModalIsOpen] = useState(false);
    const [requestRevisionModalIsOpen, setRequestRevisionModalIsOpen] = useState(false);
    const [requestRevisionMessage, setRequestRevisionMessage] = useState("");
    const [isDisabledApproveButton, setIsDisabledApproveButton] = useState(false);
    const [activityLogDrawerIsOpen, setActivityLogDrawerIsOpen] = useState(false);

    if (!cart || !account || !orderApprovalListPageLink || !cartPageLink) {
        return null;
    }

    const isBuyerEditingOrder = cart.orderApprovalStatus === "EditingInProcess";
    const isRevisionRequested = cart.orderApprovalStatus === "RevisionRequested";
    const isApprovalInProcess = cart.orderApprovalStatus === "ApprovalInProcess";

    const approvalDisabled =
        cart.cartLines?.every(cartline => cartline.isRestricted) || isDisabledApproveButton || isBuyerEditingOrder;

    const printOrOpenPrintAllModal = () => {
        openPrintDialog();
    };

    const editClickHandler = async () => {
        editOrder({
            cart,
            onSuccess: () => {
                history.push(cartPageLink.url);
            },
        });
    };

    const deleteClickHandler = () => {
        deleteOrder({
            onSuccess: () => {
                toaster.addToast({ body: translate("Order #{0} Deleted", cart.orderNumber), messageType: "success" });
                history.push(orderApprovalListPageLink.url);
            },
        });
    };

    const requestRevisionClickHandler = () => {
        setRequestRevisionModalIsOpen(true);
    };

    const requestRevisionModalCloseHandler = () => {
        setRequestRevisionModalIsOpen(false);
    };

    const approveClickHandler = () => {
        setIsDisabledApproveButton(true);

        approveOrder({
            onSuccess: () => {
                history.push(cartPageLink.url);
            },
            onComplete() {
                setTimeout(() => {
                    setIsDisabledApproveButton(false);
                }, 500);
            },
        });
    };

    const requestRevisionMessageChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setRequestRevisionMessage(event.target.value);
    };

    const sendRequestButtonClickHandler = () => {
        requestRevision({
            cart,
            requestRevisionMessage,
            onSuccess: () => {
                toaster.addToast({ body: translate("Request sent"), messageType: "success" });
            },
        });
    };

    const showActivityLogClickHandler = () => {
        setActivityLogDrawerIsOpen(true);
    };

    const closeActivityLogDrawer = () => {
        setActivityLogDrawerIsOpen(false);
    };

    const printLabel = translate("Print");
    const deleteLabel = translate("Delete");
    const editLabel = translate("Edit");
    const requestRevisionLabel = translate("Request Revision");
    const approveLabel = translate("Approve");
    const showActivityLogLabel = translate("Show Activity Log");

    // XNG-Changes: restructure
    return (
        <>
            <Hidden {...styles.menuHiddenContainer}>
                <OverflowMenu position="end" {...styles.narrowOverflowMenu}>
                    <Clickable {...styles.printClickable} onClick={printOrOpenPrintAllModal}>
                        {printLabel}
                    </Clickable>
                    <Clickable {...styles.showActivityLogClickable} onClick={showActivityLogClickHandler}>
                        {showActivityLogLabel}
                    </Clickable>
                    <Clickable
                        {...styles.deleteClickable}
                        disabled={isBuyerEditingOrder || isApprovalInProcess}
                        onClick={() => setConfirmationModalIsOpen(true)}
                    >
                        {deleteLabel}
                    </Clickable>
                    {account.canApproveOrders ? (
                        <>
                            <Clickable
                                disabled={isBuyerEditingOrder || isRevisionRequested || isApprovalInProcess}
                                {...styles.requestRevisionClickable}
                                onClick={requestRevisionClickHandler}
                            >
                                {requestRevisionLabel}
                            </Clickable>
                            <Clickable
                                disabled={approvalDisabled}
                                {...styles.approveClickable}
                                onClick={approveClickHandler}
                            >
                                {approveLabel}
                            </Clickable>
                        </>
                    ) : (
                        <>
                            <Clickable
                                {...styles.editClickable}
                                disabled={isApprovalInProcess}
                                onClick={editClickHandler}
                            >
                                {editLabel}
                            </Clickable>
                        </>
                    )}
                </OverflowMenu>
            </Hidden>
            <Hidden {...styles.buttonsHiddenContainer}>
                <StyledWrapper {...styles.buttonWrapper}>
                    <Clickable
                        data-test-selector="orderApprovalDetails_print"
                        {...styles.printButton}
                        onClick={printOrOpenPrintAllModal}
                    >
                        <Icon src={Print} {...styles.iconStyle} />
                        {printLabel}
                    </Clickable>
                    <Clickable
                        data-test-selector="orderApprovalDetails_showActivityLog"
                        {...styles.showActivityLogButton}
                        onClick={showActivityLogClickHandler}
                    >
                        {/* XNG-Changes */}
                        <Icon src={Eye} {...styles.iconStyle} />
                        {showActivityLogLabel}
                    </Clickable>
                    <Clickable
                        data-test-selector="orderApprovalDetails_deleteOrder"
                        {...styles.deleteButton}
                        disabled={isBuyerEditingOrder || isApprovalInProcess}
                        onClick={() => setConfirmationModalIsOpen(true)}
                    >
                        {/* XNG-Changes */}
                        <Icon src={Delete} {...styles.iconStyle} />
                        {deleteLabel}
                    </Clickable>
                </StyledWrapper>
                {account.canApproveOrders ? (
                    <>
                        <StyledWrapper {...styles.buttonWrapper}>
                            <Clickable
                                data-test-selector="orderApprovalDetails_requestRevision"
                                disabled={isBuyerEditingOrder || isRevisionRequested || isApprovalInProcess}
                                {...styles.requestRevisionButton}
                                onClick={requestRevisionClickHandler}
                            >
                                {/* XNG-Changes */}
                                <Icon src={Edit} {...styles.iconStyle} />
                                {requestRevisionLabel}
                            </Clickable>
                        </StyledWrapper>
                        <StyledWrapper {...styles.buttonWrapper}>
                            <Clickable
                                data-test-selector="orderApprovalDetails_approveOrder"
                                {...styles.approveButton}
                                disabled={approvalDisabled}
                                onClick={approveClickHandler}
                            >
                                <Icon src={CheckmarkOutline} {...styles.iconStyle} />
                                {approveLabel}
                            </Clickable>
                        </StyledWrapper>
                    </>
                ) : (
                    <StyledWrapper {...styles.buttonWrapper}>
                        <Clickable
                            data-test-selector="orderApprovalDetails_editOrder"
                            {...styles.editButton}
                            disabled={isApprovalInProcess}
                            onClick={editClickHandler}
                        >
                            {/* XNG-Changes */}
                            <Icon src={Edit} {...styles.iconStyle} />
                            {editLabel}
                        </Clickable>
                    </StyledWrapper>
                )}
            </Hidden>
            <TwoButtonModal
                modalIsOpen={confirmationModalIsOpen}
                headlineText={translate("Delete Order")}
                messageText={translate("Do you want to delete order #{0}?", cart.orderNumber)}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                submitTestSelector="confirmDeleteOrderButton"
                extendedStyles={styles.twoButtonModalStyles}
                onCancel={() => setConfirmationModalIsOpen(false)}
                onSubmit={deleteClickHandler}
            ></TwoButtonModal>
            <Modal
                {...styles.requestRevisionModal}
                headline={translate("Request Revision")}
                isOpen={requestRevisionModalIsOpen}
                handleClose={requestRevisionModalCloseHandler}
            >
                <GridContainer {...styles.requestRevisionContainer}>
                    <GridItem {...styles.requestRevisionMessageGridItem}>
                        <Typography {...styles.requestRevisionMessageText}>
                            {siteMessage("OrderApproval_RequestRevisionMessage")}
                        </Typography>
                        <TextArea
                            label={translate("Add a note (optional)")}
                            placeholder={translate("Write a message...")}
                            value={requestRevisionMessage}
                            onChange={requestRevisionMessageChangeHandler}
                            data-test-selector="requestRevisionMessage"
                            {...styles.requestRevisionMessageTextArea}
                        />
                    </GridItem>
                    <GridItem {...styles.requestRevisionButtonsGridItem}>
                        <Button
                            {...styles.requestRevisionCancelButton}
                            onClick={requestRevisionModalCloseHandler}
                            data-test-selector="requestRevisionModalCancel"
                        >
                            {translate("Cancel")}
                        </Button>
                        <Button
                            {...styles.requestRevisionSendRequestButton}
                            onClick={sendRequestButtonClickHandler}
                            data-test-selector="requestRevisionModalSend"
                        >
                            {translate("Send request")}
                        </Button>
                    </GridItem>
                </GridContainer>
            </Modal>
            <Drawer
                {...styles.activityLogDrawer}
                position="right"
                headline={translate("Activity")}
                isOpen={activityLogDrawerIsOpen}
                handleClose={closeActivityLogDrawer}
                contentLabel="activity log drawer"
                data-test-selector="activityLogDrawer"
            >
                <ActivityLog cart={cart} />
            </Drawer>
        </>
    );
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withHistory(withToaster(OrderApprovalDetailsActionButtons)));
